// src/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/badge.tsx
import { jsx } from "react/jsx-runtime";
var COLOR_MAP = {
  primary: { bg: "bg-accent/10", text: "text-text-brand" },
  blue: { bg: "bg-blue-100", text: "text-blue-700" },
  emerald: { bg: "bg-emerald-100", text: "text-emerald-700" },
  amber: { bg: "bg-amber-100", text: "text-amber-700" },
  red: { bg: "bg-red-100", text: "text-red-700" },
  violet: { bg: "bg-violet-100", text: "text-violet-700" },
  rose: { bg: "bg-rose-100", text: "text-rose-700" },
  teal: { bg: "bg-teal-100", text: "text-teal-700" },
  pink: { bg: "bg-pink-100", text: "text-pink-700" },
  purple: { bg: "bg-purple-100", text: "text-purple-700" },
  slate: { bg: "bg-slate-100", text: "text-slate-600" },
  green: { bg: "bg-green-100", text: "text-green-700" },
  sky: { bg: "bg-sky-100", text: "text-sky-700" }
};
var VARIANT_STYLES = {
  default: "rounded-sm px-2 py-0.5 text-[11px] font-medium",
  pill: "rounded-full px-2 py-0.5 text-[10px] font-medium",
  metric: "rounded-sm bg-surface-raised/60 px-2 py-0.5 text-[10px] font-medium text-text-tertiary",
  status: "rounded-full px-2 py-0.5 text-[10px] font-semibold"
};
function Badge({
  children,
  variant = "default",
  color,
  className
}) {
  const colorStyles = color ? COLOR_MAP[color] : null;
  const isMetric = variant === "metric";
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1",
        VARIANT_STYLES[variant],
        !isMetric && colorStyles && [colorStyles.bg, colorStyles.text],
        className
      ),
      children
    }
  );
}

// src/button.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var variants = {
  default: "bg-accent text-text-inverse hover:bg-accent/90",
  outline: "border border-stroke-default bg-surface-base hover:bg-accent-soft hover:text-text-brand",
  ghost: "hover:bg-accent-soft hover:text-text-brand",
  destructive: "bg-signal-danger text-text-inverse hover:bg-signal-danger/90"
};
var sizes = {
  default: "h-[var(--row-h,2.25rem)] px-[var(--pad-x,1rem)] py-2",
  sm: "h-8 px-3 text-xs",
  lg: "h-10 px-6",
  icon: "h-[var(--row-h,2.25rem)] w-[var(--row-h,2.25rem)]"
};
function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx2(
    "button",
    {
      className: cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-focus focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50",
        variants[variant],
        sizes[size],
        className
      ),
      ...props
    }
  );
}

// src/card.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function Card({ children, className }) {
  return /* @__PURE__ */ jsx3(
    "div",
    {
      className: cn(
        "rounded-sm border border-stroke-subtle bg-surface-raised text-text-primary",
        className
      ),
      children
    }
  );
}
function CardHeader({ children, className }) {
  return /* @__PURE__ */ jsx3("div", { className: cn("flex flex-col space-y-1.5 p-4", className), children });
}
function CardContent({ children, className }) {
  return /* @__PURE__ */ jsx3("div", { className: cn("p-4 pt-0", className), children });
}
function CardTitle({
  children,
  className
}) {
  return /* @__PURE__ */ jsx3(
    "h3",
    {
      className: cn(
        "text-sm font-semibold leading-none tracking-tight",
        className
      ),
      children
    }
  );
}

// src/numeric.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function Numeric({
  children,
  className,
  style,
  as: Tag = "span"
}) {
  return /* @__PURE__ */ jsx4(
    Tag,
    {
      className: cn("tabular-nums", className),
      style: { fontFamily: "'Outfit', sans-serif", ...style },
      children
    }
  );
}
function formatNumber(value) {
  if (value == null) return "-";
  return value.toLocaleString("ko-KR");
}
function formatScore(value) {
  if (value == null) return "-";
  return (value * 100).toFixed(1);
}

// src/score-bar.tsx
import { jsx as jsx5, jsxs } from "react/jsx-runtime";
function scoreColor(ratio) {
  if (ratio >= 0.7) return "#0d9488";
  if (ratio >= 0.4) return "#d97706";
  return "#dc2626";
}
function ScoreBar({
  value,
  max = 100,
  label,
  showValue = true,
  size = "sm",
  className
}) {
  const ratio = Math.min(value / max, 1);
  const h = size === "sm" ? "h-1.5" : "h-2.5";
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", className), children: [
    label && /* @__PURE__ */ jsx5("span", { className: "w-24 shrink-0 text-[12px] text-text-tertiary", children: label }),
    /* @__PURE__ */ jsx5("div", { className: cn("flex-1 rounded-full bg-surface-secondary", h), children: /* @__PURE__ */ jsx5(
      "div",
      {
        className: cn("rounded-full", h),
        style: {
          width: `${ratio * 100}%`,
          backgroundColor: scoreColor(ratio)
        }
      }
    ) }),
    showValue && /* @__PURE__ */ jsx5("span", { className: "w-8 text-right text-[12px] font-medium tabular-nums", children: value })
  ] });
}

// src/mini-sparkline.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function MiniSparkline({ data }) {
  if (data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const h = 28;
  const w = 64;
  const step = w / (data.length - 1);
  const points = data.map((v, i) => `${i * step},${h - (v - min) / range * h}`).join(" ");
  return /* @__PURE__ */ jsx6("svg", { width: w, height: h, className: "shrink-0 text-text-tertiary/40", children: /* @__PURE__ */ jsx6(
    "polyline",
    {
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 1.5,
      points
    }
  ) });
}

// src/stat-card.tsx
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
var trendIcon = {
  up: /* @__PURE__ */ jsx7(TrendingUp, { className: "size-3.5" }),
  down: /* @__PURE__ */ jsx7(TrendingDown, { className: "size-3.5" }),
  neutral: /* @__PURE__ */ jsx7(Minus, { className: "size-3.5" })
};
var trendColor = {
  up: "text-signal-success",
  down: "text-signal-danger",
  neutral: "text-text-tertiary"
};
function StatCard({
  label,
  value,
  unit,
  change,
  trend = "neutral",
  icon: Icon,
  iconColor,
  sparkline,
  className
}) {
  const hasIconRow = !!(Icon || sparkline);
  if (hasIconRow) {
    return /* @__PURE__ */ jsxs2(Card, { className: cn("p-4", className), children: [
      /* @__PURE__ */ jsxs2("div", { className: "flex items-center justify-between mb-3", children: [
        Icon ? /* @__PURE__ */ jsx7(
          "div",
          {
            className: cn(
              "flex size-8 items-center justify-center rounded-sm",
              iconColor ?? "bg-accent/10 text-text-brand"
            ),
            children: /* @__PURE__ */ jsx7(Icon, { className: "size-4" })
          }
        ) : /* @__PURE__ */ jsx7("div", {}),
        sparkline && /* @__PURE__ */ jsx7(MiniSparkline, { data: sparkline })
      ] }),
      /* @__PURE__ */ jsxs2(Numeric, { className: "text-2xl font-bold tracking-tight text-text-primary", children: [
        typeof value === "number" ? value.toLocaleString("ko-KR") : value,
        unit && /* @__PURE__ */ jsx7("span", { className: "text-sm font-medium text-text-tertiary ml-0.5", children: unit })
      ] }),
      /* @__PURE__ */ jsx7("p", { className: "mt-1 text-[12px] text-text-tertiary", children: label }),
      change && /* @__PURE__ */ jsxs2(
        "div",
        {
          className: cn(
            "mt-1.5 flex items-center gap-1 text-[12px] font-medium",
            trendColor[trend]
          ),
          children: [
            trendIcon[trend],
            change
          ]
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      className: cn(
        "rounded-xl border border-stroke-default bg-surface-raised p-4",
        className
      ),
      children: [
        /* @__PURE__ */ jsx7("p", { className: "text-[12px] font-medium text-text-tertiary", children: label }),
        /* @__PURE__ */ jsxs2(Numeric, { as: "p", className: "mt-1 text-2xl font-bold tracking-tight", children: [
          typeof value === "number" ? value.toLocaleString("ko-KR") : value,
          unit && /* @__PURE__ */ jsx7("span", { className: "text-sm font-medium text-text-tertiary ml-0.5", children: unit })
        ] }),
        change && /* @__PURE__ */ jsxs2(
          "div",
          {
            className: cn(
              "mt-1.5 flex items-center gap-1 text-[12px] font-medium",
              trendColor[trend]
            ),
            children: [
              trendIcon[trend],
              change
            ]
          }
        )
      ]
    }
  );
}

// src/skeleton.tsx
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
function Skeleton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8("div", { className: cn("skeleton-shimmer rounded-sm", className), ...props });
}
function StatCardSkeleton({ className }) {
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: cn(
        "rounded-xl border border-stroke-default bg-surface-raised p-4",
        className
      ),
      children: [
        /* @__PURE__ */ jsx8(Skeleton, { className: "h-3 w-20" }),
        /* @__PURE__ */ jsx8(Skeleton, { className: "mt-2.5 h-7 w-28" }),
        /* @__PURE__ */ jsx8(Skeleton, { className: "mt-3 h-3 w-24" })
      ]
    }
  );
}
function SectionCardSkeleton({
  className,
  children,
  titleWidth = "w-28",
  descWidth = "w-44"
}) {
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: cn(
        "rounded-xl border border-stroke-default bg-surface-raised p-5",
        className
      ),
      children: [
        /* @__PURE__ */ jsx8(Skeleton, { className: cn("h-4", titleWidth) }),
        /* @__PURE__ */ jsx8(Skeleton, { className: cn("mt-1.5 h-3", descWidth) }),
        /* @__PURE__ */ jsx8("div", { className: "mt-4", children: children ?? /* @__PURE__ */ jsx8(Skeleton, { className: "h-40 w-full rounded-sm" }) })
      ]
    }
  );
}
function ChartSkeleton({
  height = 200,
  className
}) {
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: cn(
        "relative overflow-hidden rounded-sm bg-surface-raised",
        className
      ),
      style: { height },
      children: [
        /* @__PURE__ */ jsx8("div", { className: "absolute left-0 top-2 flex h-[calc(100%-24px)] flex-col justify-between pb-4", children: Array.from({ length: 4 }, (_, i) => /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "h-2 w-7",
            style: { animationDelay: `${i * 200}ms` }
          },
          i
        )) }),
        /* @__PURE__ */ jsx8("div", { className: "absolute inset-0 flex items-end gap-2 pl-10 pr-2 pb-6", children: Array.from({ length: 10 }, (_, i) => /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "flex-1 rounded-t-sm",
            style: {
              height: `${30 + Math.abs(Math.sin(i * 0.9)) * 50}%`,
              animationDelay: `${i * 80}ms`
            }
          },
          i
        )) }),
        /* @__PURE__ */ jsx8("div", { className: "absolute bottom-0 left-10 right-2 flex justify-between", children: Array.from({ length: 5 }, (_, i) => /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "h-2 w-8",
            style: { animationDelay: `${i * 150}ms` }
          },
          i
        )) })
      ]
    }
  );
}
function DataTableSkeleton({
  columns = 6,
  rows = 8,
  className
}) {
  return /* @__PURE__ */ jsxs3("div", { className: cn("space-y-0", className), children: [
    /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-2 pb-3", children: [
      /* @__PURE__ */ jsx8(Skeleton, { className: "h-8 w-56 rounded-sm" }),
      /* @__PURE__ */ jsx8(Skeleton, { className: "h-8 w-20 rounded-sm" }),
      /* @__PURE__ */ jsx8("div", { className: "flex-1" }),
      /* @__PURE__ */ jsx8(Skeleton, { className: "h-8 w-16 rounded-sm" }),
      /* @__PURE__ */ jsx8(Skeleton, { className: "h-8 w-14 rounded-sm" })
    ] }),
    /* @__PURE__ */ jsxs3("div", { className: "overflow-hidden rounded-xl border border-stroke-default bg-surface-raised", children: [
      /* @__PURE__ */ jsxs3(
        "div",
        {
          className: "flex border-b border-stroke-default",
          style: { background: "hsl(var(--secondary) / 0.6)" },
          children: [
            /* @__PURE__ */ jsx8("div", { className: "w-10 border-r border-stroke-default px-2 py-2.5", children: /* @__PURE__ */ jsx8(Skeleton, { className: "mx-auto h-3 w-4" }) }),
            Array.from({ length: columns }, (_, i) => /* @__PURE__ */ jsx8(
              "div",
              {
                className: "flex-1 border-r border-stroke-default px-3 py-2.5 last:border-r-0",
                children: /* @__PURE__ */ jsx8(
                  Skeleton,
                  {
                    className: "h-3",
                    style: {
                      width: `${50 + i * 17 % 30}%`,
                      animationDelay: `${i * 60}ms`
                    }
                  }
                )
              },
              i
            ))
          ]
        }
      ),
      Array.from({ length: rows }, (_, rowIdx) => /* @__PURE__ */ jsxs3(
        "div",
        {
          className: cn(
            "flex border-b border-stroke-default last:border-0",
            rowIdx % 2 === 1 && "bg-surface-secondary/20"
          ),
          children: [
            /* @__PURE__ */ jsx8(
              "div",
              {
                className: "w-10 border-r border-stroke-default px-2 py-2.5",
                style: { background: "hsl(var(--secondary) / 0.3)" },
                children: /* @__PURE__ */ jsx8(Skeleton, { className: "mx-auto h-3 w-4" })
              }
            ),
            Array.from({ length: columns }, (_2, colIdx) => /* @__PURE__ */ jsx8(
              "div",
              {
                className: "flex-1 border-r border-stroke-default/50 px-3 py-2.5 last:border-r-0",
                children: /* @__PURE__ */ jsx8(
                  Skeleton,
                  {
                    className: "h-3",
                    style: {
                      width: `${40 + (rowIdx * 7 + colIdx * 13) % 45}%`,
                      animationDelay: `${(rowIdx * columns + colIdx) * 40}ms`
                    }
                  }
                )
              },
              colIdx
            ))
          ]
        },
        rowIdx
      ))
    ] }),
    /* @__PURE__ */ jsxs3("div", { className: "flex items-center justify-between pt-3", children: [
      /* @__PURE__ */ jsx8(Skeleton, { className: "h-3 w-32" }),
      /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx8(Skeleton, { className: "h-7 w-16 rounded-sm" }),
        /* @__PURE__ */ jsx8(Skeleton, { className: "h-7 w-28 rounded-sm" })
      ] })
    ] })
  ] });
}
function ListSkeleton({
  count = 5,
  className
}) {
  return /* @__PURE__ */ jsx8("div", { className: cn("space-y-2", className), children: Array.from({ length: count }, (_, i) => /* @__PURE__ */ jsxs3(
    "div",
    {
      className: "flex items-center gap-3 rounded-sm bg-surface-sunken/30 px-3 py-2.5",
      children: [
        /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "size-5 rounded-full",
            style: { animationDelay: `${i * 100}ms` }
          }
        ),
        /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "h-3 flex-1",
            style: {
              maxWidth: `${55 + i * 17 % 35}%`,
              animationDelay: `${i * 100}ms`
            }
          }
        ),
        /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "h-3 w-12",
            style: { animationDelay: `${i * 100}ms` }
          }
        )
      ]
    },
    i
  )) });
}
function CardListSkeleton({
  count = 4,
  className
}) {
  return /* @__PURE__ */ jsx8("div", { className: cn("space-y-3", className), children: Array.from({ length: count }, (_, i) => /* @__PURE__ */ jsxs3(
    "div",
    {
      className: "flex items-center gap-4 rounded-xl border border-stroke-default bg-surface-raised px-5 py-4",
      children: [
        /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "size-12 shrink-0 rounded-full",
            style: { animationDelay: `${i * 120}ms` }
          }
        ),
        /* @__PURE__ */ jsxs3("div", { className: "min-w-0 flex-1 space-y-2", children: [
          /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx8(
              Skeleton,
              {
                className: "h-4 w-24",
                style: { animationDelay: `${i * 120}ms` }
              }
            ),
            /* @__PURE__ */ jsx8(
              Skeleton,
              {
                className: "h-4 w-12 rounded-full",
                style: { animationDelay: `${i * 120 + 40}ms` }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-3", children: [
            /* @__PURE__ */ jsx8(
              Skeleton,
              {
                className: "h-3 w-16",
                style: { animationDelay: `${i * 120 + 80}ms` }
              }
            ),
            /* @__PURE__ */ jsx8(
              Skeleton,
              {
                className: "h-3 w-16",
                style: { animationDelay: `${i * 120 + 120}ms` }
              }
            ),
            /* @__PURE__ */ jsx8(
              Skeleton,
              {
                className: "h-3 w-16",
                style: { animationDelay: `${i * 120 + 160}ms` }
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx8(
          Skeleton,
          {
            className: "h-4 w-10 shrink-0",
            style: { animationDelay: `${i * 120}ms` }
          }
        )
      ]
    },
    i
  )) });
}
function InlineSkeleton({
  width = "w-16",
  className
}) {
  return /* @__PURE__ */ jsx8(
    "span",
    {
      className: cn(
        "skeleton-shimmer inline-block h-4 rounded align-middle",
        width,
        className
      )
    }
  );
}

// src/section-card.tsx
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
function SectionCard({
  title,
  description,
  children,
  className,
  variant = "default",
  headerClassName,
  bodyClassName
}) {
  const bare = variant === "bare";
  const hasHeader = Boolean(title || description);
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      className: cn(
        "rounded-xl border border-stroke-default bg-surface-raised",
        bare ? void 0 : "p-5",
        bare && "overflow-hidden",
        className
      ),
      children: [
        hasHeader && /* @__PURE__ */ jsxs4(
          "div",
          {
            className: cn(
              bare ? "px-4 pt-4" : void 0,
              headerClassName
            ),
            children: [
              title && /* @__PURE__ */ jsx9("h3", { className: "text-[13px] font-semibold", children: title }),
              description && /* @__PURE__ */ jsx9("p", { className: "mt-0.5 text-[12px] text-text-tertiary", children: description })
            ]
          }
        ),
        /* @__PURE__ */ jsx9(
          "div",
          {
            className: cn(
              hasHeader ? bare ? "mt-3" : "mt-4" : void 0,
              bodyClassName
            ),
            children
          }
        )
      ]
    }
  );
}

// src/action-bar.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
function ActionBar({ children, className }) {
  return /* @__PURE__ */ jsx10("div", { className: cn("flex flex-wrap items-center gap-3", className), children });
}
function ActionInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    "input",
    {
      className: cn(
        "flex-1 rounded-sm border border-stroke-default bg-surface-raised px-3 py-2 text-[13px] placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-stroke-focus/20",
        className
      ),
      ...props
    }
  );
}
function ActionTextarea({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    "textarea",
    {
      className: cn(
        "w-full rounded-sm border border-stroke-default bg-surface-raised px-3 py-2 text-[13px] placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-stroke-focus/20",
        className
      ),
      ...props
    }
  );
}
function ActionButton({
  children,
  variant = "primary",
  className,
  ...props
}) {
  const base = "inline-flex h-9 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-sm px-3 text-[13px] font-semibold leading-none transition-all disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-focus focus-visible:ring-offset-2";
  const variants3 = {
    primary: "bg-accent text-text-inverse hover:opacity-90",
    secondary: "bg-surface-secondary text-text-primary hover:bg-surface-secondary/80",
    outline: "border border-stroke-default bg-surface-raised hover:bg-surface-secondary"
  };
  return /* @__PURE__ */ jsx10("button", { className: cn(base, variants3[variant], className), ...props, children });
}

// src/empty-state.tsx
import { jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
function EmptyState({ message }) {
  return /* @__PURE__ */ jsxs5("div", { className: "flex flex-col items-center justify-center py-8 text-center", children: [
    /* @__PURE__ */ jsx11("div", { className: "text-3xl opacity-20 mb-2", children: "\u{1F50D}" }),
    /* @__PURE__ */ jsx11("p", { className: "text-sm font-medium text-text-tertiary", children: message ?? "\uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4" })
  ] });
}

// src/page-title.tsx
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
function PageTitle({
  title,
  count,
  badge
}) {
  return /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 mb-3", children: [
    /* @__PURE__ */ jsx12("h3", { className: "text-base font-semibold", children: title }),
    count != null && /* @__PURE__ */ jsxs6(Badge, { color: "slate", variant: "pill", children: [
      count.toLocaleString(),
      "\uAC74"
    ] }),
    badge && /* @__PURE__ */ jsx12(Badge, { color: badge.color ?? "blue", variant: "pill", children: badge.label })
  ] });
}

// src/persist-badge.tsx
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
function PersistBadge({
  label,
  count
}) {
  if (count == null || count === 0) return null;
  return /* @__PURE__ */ jsxs7(Badge, { color: "emerald", variant: "status", children: [
    label,
    " ",
    count.toLocaleString(),
    "\uAC74"
  ] });
}
function PersistStats({
  persist
}) {
  if (!persist) return null;
  const entries = Object.entries(persist).filter(([, v]) => v > 0);
  if (!entries.length) return null;
  return /* @__PURE__ */ jsx13("div", { className: "flex flex-wrap gap-1.5 mt-3", children: entries.map(([key, count]) => /* @__PURE__ */ jsx13(PersistBadge, { label: key, count }, key)) });
}

// src/trend-indicator.tsx
import { jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
function TrendIndicator({
  value,
  suffix = "",
  inverse = false
}) {
  if (value == null || value === 0) {
    return /* @__PURE__ */ jsx14("span", { className: "text-xs text-text-tertiary", children: "-" });
  }
  const isPositive = inverse ? value < 0 : value > 0;
  const arrow = value > 0 ? "\u2191" : "\u2193";
  const absValue = Math.abs(value);
  return /* @__PURE__ */ jsxs8(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-0.5 text-xs font-medium",
        isPositive ? "text-signal-success" : "text-signal-danger"
      ),
      children: [
        arrow,
        /* @__PURE__ */ jsxs8(Numeric, { children: [
          absValue.toLocaleString(),
          suffix
        ] })
      ]
    }
  );
}

// src/input.tsx
import { jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var BASE = "w-full rounded-sm border border-stroke-default bg-surface-input text-text-primary h-[var(--row-h,2.25rem)] text-sm outline-none ring-stroke-focus transition-colors placeholder:text-text-tertiary focus-visible:ring-2 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";
var DEFAULT_PX = "px-[var(--pad-x,0.75rem)]";
function Input({
  className,
  type = "text",
  leadingIcon,
  trailingIcon,
  ...props
}) {
  if (!leadingIcon && !trailingIcon) {
    return /* @__PURE__ */ jsx15(
      "input",
      {
        type,
        className: cn(BASE, DEFAULT_PX, className),
        ...props
      }
    );
  }
  return /* @__PURE__ */ jsxs9("div", { className: "relative flex w-full items-center", children: [
    leadingIcon && /* @__PURE__ */ jsx15(
      "span",
      {
        className: "pointer-events-none absolute left-3 flex items-center justify-center text-text-tertiary",
        "aria-hidden": true,
        children: leadingIcon
      }
    ),
    /* @__PURE__ */ jsx15(
      "input",
      {
        type,
        className: cn(
          BASE,
          leadingIcon ? "pl-10" : "",
          trailingIcon ? "pr-10" : "",
          !leadingIcon && !trailingIcon && DEFAULT_PX,
          leadingIcon && !trailingIcon && "pr-[var(--pad-x,0.75rem)]",
          !leadingIcon && trailingIcon && "pl-[var(--pad-x,0.75rem)]",
          className
        ),
        ...props
      }
    ),
    trailingIcon && /* @__PURE__ */ jsx15("span", { className: "absolute right-3 flex items-center justify-center", children: trailingIcon })
  ] });
}

// src/textarea.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
function Textarea({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx16(
    "textarea",
    {
      className: cn(
        "min-h-[80px] w-full rounded-sm border border-stroke-default bg-surface-base px-3 py-2.5 text-[13px] outline-none ring-stroke-focus transition-shadow placeholder:text-text-tertiary focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  );
}

// src/label.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
function Label({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx17(
    "label",
    {
      className: cn(
        "text-[12px] font-semibold text-text-primary peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        className
      ),
      ...props
    }
  );
}

// src/separator.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
function Separator({
  className,
  orientation = "horizontal",
  decorative = false,
  ...props
}) {
  return /* @__PURE__ */ jsx18(
    "div",
    {
      role: decorative ? "none" : "separator",
      "aria-orientation": decorative ? void 0 : orientation,
      className: cn(
        "shrink-0 bg-stroke-default",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  );
}

// src/alert.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var variants2 = {
  default: "bg-surface-base text-text-primary border-stroke-default",
  info: "bg-blue-50 text-blue-900 border-blue-200",
  success: "bg-emerald-50 text-emerald-900 border-emerald-200",
  warning: "bg-amber-50 text-amber-900 border-amber-200",
  destructive: "bg-red-50 text-red-900 border-red-200"
};
function Alert({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    "div",
    {
      role: "alert",
      className: cn(
        "relative w-full rounded-sm border p-4 text-[13px]",
        variants2[variant],
        className
      ),
      ...props
    }
  );
}
function AlertTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    "h5",
    {
      className: cn(
        "mb-1 font-semibold leading-none tracking-tight",
        className
      ),
      ...props
    }
  );
}
function AlertDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    "p",
    {
      className: cn(
        "text-[12px] leading-relaxed [&_p]:leading-relaxed",
        className
      ),
      ...props
    }
  );
}

// src/progress.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
function Progress({
  value,
  max = 100,
  className,
  ...props
}) {
  const pct = Math.min(Math.max(value / max * 100, 0), 100);
  return /* @__PURE__ */ jsx20(
    "div",
    {
      role: "progressbar",
      "aria-valuenow": value,
      "aria-valuemin": 0,
      "aria-valuemax": max,
      className: cn(
        "relative h-2 w-full overflow-hidden rounded-full bg-surface-secondary",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx20(
        "div",
        {
          className: "h-full rounded-full bg-accent transition-all",
          style: { width: `${pct}%` }
        }
      )
    }
  );
}

// src/layout-header.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
function LayoutHeader({ children, className }) {
  return /* @__PURE__ */ jsx21(
    "header",
    {
      className: cn(
        "flex h-14 shrink-0 items-center gap-3 border-b border-stroke-default bg-surface-raised px-4",
        className
      ),
      children
    }
  );
}

// src/panel-toggle-button.tsx
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { jsx as jsx22 } from "react/jsx-runtime";
function PanelToggleButton({
  isOpen,
  onToggle,
  className
}) {
  return /* @__PURE__ */ jsx22(
    "button",
    {
      type: "button",
      onClick: onToggle,
      title: isOpen ? "\uD328\uB110 \uC811\uAE30" : "\uD328\uB110 \uC5F4\uAE30",
      className: cn(
        "flex size-8 shrink-0 items-center justify-center rounded-sm transition-colors",
        isOpen ? "bg-accent/10 text-text-brand" : "text-text-tertiary hover:bg-surface-secondary hover:text-text-primary",
        className
      ),
      children: isOpen ? /* @__PURE__ */ jsx22(PanelLeftClose, { className: "size-4" }) : /* @__PURE__ */ jsx22(PanelLeftOpen, { className: "size-4" })
    }
  );
}

// src/use-persistent-state.ts
import { useState, useEffect, useCallback } from "react";
function usePersistentState(key, defaultValue) {
  const [value, setValueRaw] = useState(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return defaultValue;
      return JSON.parse(stored);
    } catch {
      return defaultValue;
    }
  });
  useEffect(() => {
    try {
      if (value === null || value === void 0) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch {
    }
  }, [key, value]);
  const setValue = useCallback((next) => {
    setValueRaw(next);
  }, []);
  return [value, setValue];
}

// src/brand-logo.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
function BrandLogo({
  className,
  style
}) {
  return /* @__PURE__ */ jsx23(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 512 512",
      className,
      style,
      children: /* @__PURE__ */ jsx23(
        "g",
        {
          transform: "translate(0,512) scale(0.1,-0.1)",
          fill: "currentColor",
          stroke: "none",
          children: /* @__PURE__ */ jsx23("path", { d: "M440 5109 c-132 -19 -256 -93 -334 -199 -56 -75 -84 -151 -97 -254 -6 -51 -9 -855 -7 -2156 l3 -2075 22 -65 c55 -162 177 -282 337 -333 l71 -22 2125 0 2125 0 71 22 c163 52 285 174 337 338 l22 70 0 2125 0 2125 -22 71 c-51 160 -171 282 -333 337 l-65 22 -2100 1 c-1155 1 -2125 -2 -2155 -7z m2945 -980 c291 -27 587 -123 745 -241 232 -174 392 -431 407 -654 23 -337 -306 -924 -786 -1404 -170 -170 -263 -246 -456 -375 -410 -274 -796 -419 -1174 -441 -95 -6 -112 -4 -165 16 -90 34 -158 106 -185 194 -26 84 -27 147 -2 218 27 79 46 111 96 168 46 51 55 67 33 54 -10 -6 -586 -437 -880 -659 -32 -24 -50 -30 -89 -30 -47 0 -52 3 -111 62 -74 74 -77 96 -30 195 86 183 542 809 823 1129 373 427 814 834 1291 1197 136 103 168 113 246 77 75 -34 92 -51 92 -91 0 -29 -16 -48 -151 -176 -141 -135 -518 -523 -820 -848 -308 -330 -556 -615 -698 -800 -85 -111 -83 -125 9 -63 205 138 722 413 778 413 24 0 39 -12 86 -71 35 -44 56 -80 56 -96 0 -19 -31 -58 -119 -149 -136 -141 -211 -236 -211 -266 0 -20 4 -21 73 -15 271 20 842 347 1318 754 162 138 391 369 490 495 178 224 263 431 229 554 -17 59 -70 129 -151 200 -226 197 -568 278 -1039 245 -308 -22 -587 -78 -892 -181 -324 -110 -671 -287 -970 -497 -178 -125 -345 -295 -336 -343 16 -83 734 245 996 456 80 64 158 108 177 100 49 -18 -21 -144 -173 -312 -280 -312 -565 -496 -825 -535 -61 -9 -85 -8 -137 5 -154 40 -295 164 -334 296 -63 212 62 480 324 693 335 272 786 483 1325 620 444 113 755 142 1140 106z" })
        }
      )
    }
  );
}

// src/app-breadcrumb.tsx
import { ChevronRight } from "lucide-react";
import { jsx as jsx24, jsxs as jsxs10 } from "react/jsx-runtime";
function AppBreadcrumb({ segments }) {
  if (segments.length === 0) return null;
  return /* @__PURE__ */ jsx24("div", { className: "flex items-center gap-1.5 overflow-x-auto text-[11px] font-medium text-text-tertiary", children: segments.map((segment, index) => /* @__PURE__ */ jsxs10(
    "div",
    {
      className: "flex items-center gap-1.5 whitespace-nowrap",
      children: [
        index > 0 && /* @__PURE__ */ jsx24(ChevronRight, { className: "size-3 text-text-tertiary/50" }),
        /* @__PURE__ */ jsx24(
          "span",
          {
            className: cn(index === segments.length - 1 && "text-text-primary"),
            children: segment
          }
        )
      ]
    },
    `${segment}-${index}`
  )) });
}

export {
  cn,
  Badge,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  Numeric,
  formatNumber,
  formatScore,
  ScoreBar,
  MiniSparkline,
  StatCard,
  Skeleton,
  StatCardSkeleton,
  SectionCardSkeleton,
  ChartSkeleton,
  DataTableSkeleton,
  ListSkeleton,
  CardListSkeleton,
  InlineSkeleton,
  SectionCard,
  ActionBar,
  ActionInput,
  ActionTextarea,
  ActionButton,
  EmptyState,
  PageTitle,
  PersistBadge,
  PersistStats,
  TrendIndicator,
  Input,
  Textarea,
  Label,
  Separator,
  Alert,
  AlertTitle,
  AlertDescription,
  Progress,
  LayoutHeader,
  PanelToggleButton,
  usePersistentState,
  BrandLogo,
  AppBreadcrumb
};
//# sourceMappingURL=chunk-6PEP2SRX.js.map